import React, {useState, useEffect, useRef, forwardRef, useImperativeHandle} from 'react';
import {useNavigate} from 'react-router-dom';
import {ActionSheet, Button, Card, List, Toast, Skeleton, Dialog } from 'antd-mobile';
import {getBottomNavBarContainerStyle} from "../../../../utils/deviceUtils";
import {DeliveringPath, DeliveryDetailPath} from "../../../../enums/DeliveryNavigatePathEnum";
import EmptyState from "../../../../components/EmptyState";
import {useCustomToast} from 'hooks/useCustomToast';
import {useDeliveryTaskOrder, useExceptionDictionaries} from "../../store/deliveryStore";
import {timestampToDate} from "../../../../utils/dateTimeUtils";
import {formatLocalizedDateTime} from "../../../../utils/dateFormatUtils";
import {ExceptionDictionaryType} from '../../graphql/queries/getExceptionDictionaryForDelivery';
import {t} from "../../../../i18n";
import {useTranslation} from "react-i18next";
import { copyToClipboard } from 'utils/copyToClipboard';
import { useNetwork } from 'hooks/useNetwork';
import { getExceptionTypeText } from '../../../../utils/i18nUtils';
import { formatStopNumberFromDeliveryTaskOrder } from '../../../../utils/stopNumberUtils';
import { isAppVersionGreaterThanOrEqual, kNativeScanVersion} from 'utils/versionUtils';
import { openNativeScan } from 'utils/appRuntime';
import AccessInfoDisplay, { AccessCode } from './AccessInfoDisplay';
import OrderNoteItem from '../../../../components/OrderNoteItem/OrderNoteItem';
import { maskPhoneNumber } from '../../../../utils/phoneUtils';
import DeliveryPhoneAndNavigate, { DeliveryPhoneAndNavigateRef } from "./DeliveryPhoneAndNavigate";
import AccessCodeValidationDialog from './AccessCodeValidationDialog';
import { useAccessCodes } from '../hooks/useAccessCodes';
import { validateAccessCodeLocation } from '../utils/accessCodeLocationValidation';
import { getAndClearShouldCheckPendingCodes } from '../utils/accessCodeValidationState';
import {getUserInfo} from "../../../../utils/auth";
import {closeNavigationModals as closeNavigationModalsUtil, forceCloseNavigationModal} from "../../../../utils/navigationUtils";

interface OrderDetailCardProps {
    deliveryTaskOrderId: number;
    trackingNumber: string;
    onClose?: () => void; // 可选的关闭回调
    onBeforeNavigate?: () => void; // 跳转前的回调，用于保存地图状态
    fromMapMode?: boolean; // 是否来自地图模式
    onNavigateToAccessCodeDetail?: (params: {
        orderId: number;
        addressId?: number;
        trackingNumber?: string;
        address?: string;
        addressLat?: number;
        addressLon?: number;
    }) => void; // 导航到门禁详情的回调（地图模式使用）
    onNavigateToAddForm?: (params: {
        orderId: number;
        addressId?: number;
        trackingNumber?: string;
        address?: string;
        addressLat?: number;
        addressLon?: number;
    }) => void; // 导航到新增表单的回调（地图模式使用）
    clusterInfo?: { // 新增集群信息
        isCluster?: boolean;
        clusterCount?: number;
        clusterAddresses?: any[];
    };
    popupContentView?: 'order' | 'accessCode' | 'addForm' | 'updateForm'; // Popup 内容视图（地图模式使用，用于触发待验证code检查）
    analyzedAddressLine1?: string; // 从父组件传入的分析后的地址（MapModePage使用）
    isNavigationAddressAnalyzeEnabled?: boolean;
}

export interface OrderDetailCardRef {
    closeLongPressModal: () => void;
}

const OrderDetailCard = forwardRef<OrderDetailCardRef, OrderDetailCardProps>(({ 
    deliveryTaskOrderId, 
    trackingNumber,
    onClose,
    onBeforeNavigate,
    fromMapMode,
    onNavigateToAccessCodeDetail,
    onNavigateToAddForm,
    clusterInfo,
    popupContentView,
    analyzedAddressLine1,
    isNavigationAddressAnalyzeEnabled
}, ref) => {
    const navigate = useNavigate();
    const deliveryPhoneAndNavigateRef = useRef<DeliveryPhoneAndNavigateRef>(null);
    const { i18n, t } = useTranslation();

    // 暴露关闭长按弹窗的方法给父组件
    useImperativeHandle(ref, () => ({
        closeLongPressModal: () => {
            // 单个包裹模式：关闭单个导航弹窗
            deliveryPhoneAndNavigateRef.current?.closeLongPressModal();
            // 集群模式：通过 DOM 查询关闭所有打开的导航弹窗（iOS 18 增强）
            closeNavigationModalsUtil();
            // iOS 18 需要更强制的方式关闭弹窗，使用 requestAnimationFrame 确保状态更新后再执行 DOM 操作
            requestAnimationFrame(() => {
                forceCloseNavigationModal(
                    t('pages.delivery.orderDetail.selectMapForThisTimeTitle') || undefined,
                    t('pages.delivery.orderDetail.mapCancel') || undefined
                );
            });
        }
    }), []);
    const {showToast} = useCustomToast();
    const {deliveryTaskOrder, loading} = useDeliveryTaskOrder(deliveryTaskOrderId);
    const exceptionDictionaries = useExceptionDictionaries();
    const [failedActionSheetVisible, setFailedActionSheetVisible] = useState(false);
    const [addAccessInfoDialogVisible, setAddAccessInfoDialogVisible] = useState(false);
    const [isValidatingLocation, setIsValidatingLocation] = useState(false);
    const { isOnline } = useNetwork();

    // 处理新增Code按钮点击（先校验地址）
    const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const handleAddCodeClick = async () => {
        const addressLat = deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.latitude as number | undefined;
        const addressLon = deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.longitude as number | undefined;

        // 延迟显示loading（200ms），如果响应很快则不显示，避免闪烁
        loadingTimeoutRef.current = setTimeout(() => {
            setIsValidatingLocation(true);
        }, 200);

        try {
            await validateAccessCodeLocation({
                addressLat,
                addressLon,
                onValidationPassed: () => {
                    // 清除延迟显示loading的定时器
                    if (loadingTimeoutRef.current) {
                        clearTimeout(loadingTimeoutRef.current);
                        loadingTimeoutRef.current = null;
                    }
                    setIsValidatingLocation(false);
                    setAddAccessInfoDialogVisible(true);
                },
                onValidationFailed: () => {
                    // 清除延迟显示loading的定时器
                    if (loadingTimeoutRef.current) {
                        clearTimeout(loadingTimeoutRef.current);
                        loadingTimeoutRef.current = null;
                    }
                    setIsValidatingLocation(false);
                },
                isFormSubmit: false
            });
        } catch (error) {
            // 清除延迟显示loading的定时器
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
                loadingTimeoutRef.current = null;
            }
            setIsValidatingLocation(false);
            console.error('位置校验失败:', error);
        }
    };

    // 门禁码管理Hook
    const {
        accessCodes,
        pendingAccessCodes,
        validationDialogVisible,
        setValidationDialogVisible,
        handleValidationComplete,
        checkPendingAccessCodes,
        isLoadingAccessCodes,
        isFeatureEnabled,
        isCheckingFeature
    } = useAccessCodes({
        orderId: deliveryTaskOrder?.orderWithAddress?.order?.id,
        loading,
        addressLat: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.latitude as number | undefined,
        addressLon: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.longitude as number | undefined
    });

    // 当 deliveryTaskOrderId 变化时，重置弹框状态（防止切换stop时弹框残留）
    useEffect(() => {
        setAddAccessInfoDialogVisible(false);
        setValidationDialogVisible(false);
    }, [deliveryTaskOrderId, setValidationDialogVisible]);

    // 检测从AccessInfoDetailView返回，触发验证弹窗（地图模式）
    // 与 OrderDetailPage 的逻辑保持一致
    // 暂时禁用：本期先不考虑反馈弹框功能，但代码保留
    // useEffect(() => {
    //     // 只在 popupContentView === 'order' 时检查（地图模式）
    //     if (popupContentView === 'order' && accessCodes.length > 0 && !loading && checkPendingAccessCodes) {
    //         if (getAndClearShouldCheckPendingCodes()) {
    //             setTimeout(() => {
    //                 checkPendingAccessCodes();
    //             }, 300);
    //         }
    //     }
    // }, [popupContentView, accessCodes.length, loading, checkPendingAccessCodes]);
    const getFormattedStopNumber = () => {
        if (!deliveryTaskOrder) return '0';

        const originalStopNumber = formatStopNumberFromDeliveryTaskOrder(deliveryTaskOrder);

        if (!clusterInfo?.isCluster || !clusterInfo?.clusterAddresses) {
            return originalStopNumber;
        }

        // 创建字母映射
        const allStopNumbers = clusterInfo.clusterAddresses
            .map((addr: any) => formatStopNumberFromDeliveryTaskOrder(addr));

        const groups: {[key: string]: string[]} = {};
        allStopNumbers.forEach((sn: string) => {
            const match = sn.match(/^(\d+)-/);
            if (match) {
                const mainNum = match[1];
                if (!groups[mainNum]) {
                    groups[mainNum] = [];
                }
                groups[mainNum].push(sn);
            }
        });

        const suffixMap = new Map();

        Object.keys(groups).forEach(mainNum => {
            const groupNumbers = groups[mainNum];

            // 分离字母后缀和数字后缀
            const letterSuffixes: string[] = [];
            const numberSuffixes: string[] = [];

            groupNumbers.forEach(sn => {
                const suffix = sn.split('-')[1];
                if (/[a-zA-Z]/.test(suffix)) {
                    letterSuffixes.push(sn);
                } else {
                    numberSuffixes.push(sn);
                }
            });

            // 对字母后缀按字母顺序排序
            const sortedLetterSuffixes = letterSuffixes.sort((x: string, y: string) => {
                const getSuffixValue = (sn: string) => {
                    const suffix = sn.split('-')[1];
                    return suffix.toLowerCase().charCodeAt(0) - 96;
                };
                return getSuffixValue(x) - getSuffixValue(y);
            });

            // 对数字后缀按数字大小排序
            const sortedNumberSuffixes = numberSuffixes.sort((x: string, y: string) => {
                const getSuffixValue = (sn: string) => {
                    const suffix = sn.split('-')[1];
                    return parseInt(suffix);
                };
                return getSuffixValue(x) - getSuffixValue(y);
            });

            // 合并两组：字母在前，数字在后
            const allSorted = [...sortedLetterSuffixes, ...sortedNumberSuffixes];

            // 所有编号保持原样，不进行映射
            allSorted.forEach((sn: string) => {
                suffixMap.set(sn, sn);
            });
        });

        return suffixMap.get(originalStopNumber) || originalStopNumber;
    };

    const handleDeliverySuccess = async () => {
        // 跳转前保存地图状态
        onBeforeNavigate?.();
        
        const currentTrackingNumber = deliveryTaskOrder?.trackingNumber || trackingNumber;

        // 检查版本是否支持原生扫码
        const supportsNativeScan = await isAppVersionGreaterThanOrEqual(kNativeScanVersion);
        if (supportsNativeScan) {
            openNativeScan(currentTrackingNumber, () => {
                navigateOrderDetail(currentTrackingNumber)
            });
        } else {
            if (!isOnline) {
                // 无网情况：直接跳转派送成功页面
                navigateOrderDetail(currentTrackingNumber)
            } else {
                // 有网情况：先跳转到ScanPackageVerification页面进行二次确认
                const targetPath = DeliveryDetailPath.DELIVERY_DELIVERING_ORDER_DETAIL.replace(':trackingNumber', currentTrackingNumber);
                const targetState = {
                    deliveryTaskOrderId: deliveryTaskOrderId,
                    trackingNumber: currentTrackingNumber,
                    detailType: 'delivered',
                    fromMapMode: fromMapMode
                };
                navigate(DeliveringPath.DELIVERY_SCAN_PACKAGE, {
                    state: {
                        targetPath: targetPath,
                        targetState: targetState
                    }
                });
            }
        }

        onClose?.(); // 关闭弹窗
    };

    const navigateOrderDetail = (currentTrackingNumber: string) => {
        navigate(DeliveryDetailPath.DELIVERY_DELIVERING_ORDER_DETAIL.replace(':trackingNumber', currentTrackingNumber),{
            state:{
                deliveryTaskOrderId: deliveryTaskOrderId,
                trackingNumber:currentTrackingNumber,
                detailType: 'delivered',
                fromMapMode: fromMapMode
            }
        });
    }

    const handleFailedActionSheetVisible = async () => {
        if (exceptionDictionaries.length === 0){
            showToast('fail', 'No exception actions available.', 'center');
            return;
        }
        setFailedActionSheetVisible(true);
    };

      // 处理来自原生的派送成功回调
    const handleDeliverySuccessFromNative = () => {
        handleDeliverySuccess();
    };

    // 处理来自原生的派送失败回调
    const handleDeliveryFailedFromNative = () => {
        handleFailedActionSheetVisible();
    };
    
    const handleActionClick = async (action: ExceptionDictionaryType) => {
        setFailedActionSheetVisible(false);
        
        // 跳转前保存地图状态
        onBeforeNavigate?.();
        
        const currentTrackingNumber = deliveryTaskOrder?.trackingNumber || trackingNumber;
        navigate(DeliveringPath.DELIVERY_MARK_EXCEPTION,{
            state:{
                deliveryTaskOrderId: deliveryTaskOrderId,
                trackingNumber:currentTrackingNumber,
                exceptionReason: action,
                fromMapMode: fromMapMode
            }
        });
        onClose?.(); // 关闭弹窗
    };


    const handleCopyAddress = async () => {
        if (!address || address.trim() === '') {
            Toast.show({
                content: t('pages.delivery.delivering.noAddress'),
                position: 'bottom',
            });
            return;
        }

        try {
            const success = await copyToClipboard(address);
            if (success) {
                Toast.show({
                    content: t('pages.delivery.delivering.addressCopied'),
                    position: 'bottom',
                });
            } else {
                Toast.show({
                    content: t('pages.delivery.delivering.copyFailed'),
                    position: 'bottom',
                });
            }
        } catch (error) {
            console.error('Copy address failed:', error);
            Toast.show({
                content: t('pages.delivery.delivering.copyFailed'),
                position: 'bottom',
            });
        }
    };

    if (loading) {
        return (
            <div className="p-4">
                <Skeleton style={{width: '100%',height: '45%'}} animated />
                <Skeleton.Paragraph lineCount={5} animated />
                <Skeleton.Title style={{width: '100%'}} animated />
                <Skeleton.Title style={{width: '100%'}} animated />
                <Skeleton.Title style={{width: '100%'}} animated />
            </div>
        );
    }

    if (!deliveryTaskOrder) {
        return (
            <div className="p-4">
                <EmptyState/>
            </div>
        );
    }

    const address = [
        deliveryTaskOrder.orderWithAddress?.deliveryAddress?.addressLine1 || '',
        deliveryTaskOrder.orderWithAddress?.deliveryAddress?.addressLine2 || '',
        deliveryTaskOrder.orderWithAddress?.deliveryAddress?.city || '',
        deliveryTaskOrder.orderWithAddress?.deliveryAddress?.state || '',
        deliveryTaskOrder.orderWithAddress?.deliveryAddress?.zipcode || '',
        'US'
    ].filter(Boolean).join(' ');
    const stopNumber = getFormattedStopNumber(); // 使用新的格式化函数
    const fullName = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.fullName;
    const phoneNumberParts = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.phoneNumber?.split(',') || [];
    const phone = phoneNumberParts[0] || deliveryTaskOrder.orderWithAddress?.deliveryAddress?.phoneNumber;
    const extension = phoneNumberParts[1] ? phoneNumberParts[1].replace('#', '') : deliveryTaskOrder.orderWithAddress?.deliveryAddress?.extension;
    const routeNumber = deliveryTaskOrder.routeName;
    const deliverAt = deliveryTaskOrder.orderWithAddress?.order?.deliverAt;
    const deliveryAttempts = deliveryTaskOrder.orderWithAddress?.order?.deliveryAttempts || 0;
    const isAddressChanged = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.isAddressChanged;
    const userInfo = getUserInfo();
    return (
        <div className="flex flex-col h-full relative">
            {/* 位置校验Loading遮罩 */}
            {isValidatingLocation && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 shadow-lg">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent mx-auto mb-3"/>
                            <div className="text-gray-700 text-base">{t('pages.delivery.orderDetail.loading')}</div>
                        </div>
                    </div>
                </div>
            )}
            {/* 可滚动的内容区域 */}
            <div className="flex-1 overflow-y-auto">
                {/* Order Details Card */}
                <Card className="m-2 rounded-lg flex flex-col">
                    {/* 置顶的 trackingNumber */}
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-700 break-all word-break-break-all hyphens-auto leading-relaxed">{deliveryTaskOrder?.trackingNumber || trackingNumber}</h3>
                    </div>

                    {/* 主要内容区域 */}
                    <div className="flex items-start px-4 py-4 gap-3 sm:gap-4">
                        {/* 左边居中的 stopNumber - 与 trackingNumber 左对齐 */}
                        <div className="flex flex-col items-center justify-center flex-shrink-0 min-w-[60px] sm:min-w-[80px] w-[60px] sm:w-20 h-24">
                            <p className="text-2xl font-bold whitespace-nowrap mb-2">{stopNumber}</p>
                            <p className="text-sm text-gray-500 text-center leading-tight">{t('pages.stop')}</p>
                        </div>
                        {/* 详细信息 - 尽量展开，优化为两组信息 */}
                        <div className="flex flex-col gap-3 flex-1 min-w-0">
                            {/* 第一组：主-收件人名称；副-手机号 */}
                            <div className="flex flex-col gap-1">
                                <h3 className="text-lg font-medium break-words">{fullName}</h3>
                                <div className="text-sm text-gray-500 break-all">{`${maskPhoneNumber(phone)}${extension ? `-${extension}` : ''}`}</div>
                            </div>
                            {/* 第二组：主-运单地址；副-门禁信息 */}
                            <div className="flex flex-col gap-1">
                                <div className="text-blue-500 text-base">
                                    <div className="flex items-start gap-2">
                                        {isAddressChanged && (
                                            <span className="px-2 py-0.5 text-xs font-medium text-white bg-red-500 rounded whitespace-nowrap mt-1">
                                                {t('pages.delivery.orderDetail.addressUpdated')}
                                            </span>
                                        )}
                                        <span className="break-words flex-1 min-w-0 leading-relaxed">{address}</span>
                                        <button
                                            onClick={handleCopyAddress}
                                            className="p-1 flex items-center justify-center flex-shrink-0"
                                            style={{ minWidth: '24px', height: '24px' }}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M7 4V2C7 1.45 7.45 1 8 1H20C20.55 1 21 1.45 21 2V16C21 16.55 20.55 17 20 17H18V19C18 20.1 17.1 21 16 21H4C2.9 21 2 20.1 2 19V7C2 5.9 2.9 5 4 5H7V4M7 7H4V19H16V7H7Z"/>
                                            </svg>
                                        </button>
                                    </div>
                                    {isAddressChanged && (
                                        <div className="text-xs text-red-500 mt-1">{t('pages.delivery.orderDetail.addressUpdatedHint')}</div>
                                    )}
                                </div>
                                {/* 门禁信息展示 */}
                                <AccessInfoDisplay
                                    accessCodes={accessCodes}
                                    addressId={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.id}
                                    trackingNumber={deliveryTaskOrder?.trackingNumber || trackingNumber}
                                    address={address}
                                    addressLat={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.latitude as number | undefined}
                                    addressLon={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.longitude as number | undefined}
                                    orderId={deliveryTaskOrder?.orderWithAddress?.order?.id}
                                    onAddCode={handleAddCodeClick}
                                    showAddButton={true}
                                    isLoading={isLoadingAccessCodes || isCheckingFeature}
                                    fromMapMode={fromMapMode}
                                    deliveryTaskOrderId={deliveryTaskOrderId}
                                    onNavigateToAccessCodeDetail={onNavigateToAccessCodeDetail}
                                    isFeatureEnabled={isFeatureEnabled}
                                />
                            </div>
                        </div>
                    </div>

                    <DeliveryPhoneAndNavigate
                        ref={deliveryPhoneAndNavigateRef}
                        tracking={deliveryTaskOrder?.trackingNumber || trackingNumber}
                        phone={phone || ''}
                        address={address}
                        addressLine1={analyzedAddressLine1}
                        isNavigationAddressAnalyzeEnabled={isNavigationAddressAnalyzeEnabled}
                        longitude={deliveryTaskOrder.orderWithAddress?.deliveryAddress?.longitude}
                        latitude={deliveryTaskOrder.orderWithAddress?.deliveryAddress?.latitude}
                        stopNumber={deliveryTaskOrder.stopNumber.toString()}
                        fullName={fullName}
                        subStopNumber={deliveryTaskOrder?.subStopNumber}
                        driverId={parseInt(getUserInfo()!!.account.id)}
                        driverName={getUserInfo()!!.account.name}
                        onDeliverySuccessFromNative={handleDeliverySuccessFromNative}
                        onDeliveryFailedFromNative={handleDeliveryFailedFromNative}
                        orderAddressId={deliveryTaskOrder.orderWithAddress.deliveryAddress.id}
                        dspId={deliveryTaskOrder.dspId}
                    />

                    <List
                        style={{
                            '--border-top': 'none',
                            '--border-bottom': 'none',
                            '--padding-left' : '0px',
                            '--padding-right' : '0px',
                        }}
                    >
                        <List.Item title={`${t('pages.delivery.orderDetail.route')}:`} extra={routeNumber || '-'} />
                        <List.Item title={`${t('pages.delivery.orderDetail.assignedTime')}:`} extra={deliverAt ? formatLocalizedDateTime(timestampToDate(deliverAt)) : ''}/>
                        <List.Item title={`${t('pages.delivery.orderDetail.deliveryAttempt')}:`} extra={`${deliveryAttempts} out of 3`}/>
                        <List.Item
                            className="[&_.adm-list-item-content-main]:p-0"
                            title=""
                            extra={
                                <OrderNoteItem 
                                    orderNote={deliveryTaskOrder?.orderNote}
                                    currentLanguage={i18n.language}
                                    className="w-full"
                                />
                            }
                        />
                    </List>
                </Card>
            </div>

            {/* 固定在底部的按钮区域 */}
            <div {...getBottomNavBarContainerStyle('flex px-4 py-2 pb-4 gap-4 flex-shrink-0')}>
                <Button
                    color='default'
                    onClick={handleFailedActionSheetVisible}
                    block
                    style={{
                        '--background-color': 'gray',
                        '--text-color': 'white'
                    }}
                >
                    {t('pages.delivery.orderDetail.failed')}
                </Button>
                <Button
                    color='primary'
                    onClick={handleDeliverySuccess}
                    block
                >
                    {t('pages.delivery.orderDetail.delivered')}
                </Button>
            </div>

            <ActionSheet
                visible={failedActionSheetVisible}
                extra={
                    <div className="text-gray-500">
                        <h3 className={`text-center`}>{t('pages.delivery.orderDetail.failed')}</h3>
                        <span>{t('pages.delivery.orderDetail.failedDesc')}</span>
                    </div>
                }
                cancelText={t('pages.delivery.detail.cancel')}
                onClose={() => setFailedActionSheetVisible(false)}
                actions={exceptionDictionaries.map(action => ({
                    text: (
                        <div>
                            <div className="text-purple-700 font-medium text-lg">
                                {getExceptionTypeText(action)}
                            </div>
                        </div>
                    ),
                    key: action.exceptionReasonCode,
                    onClick: () => handleActionClick(action)
                }))}
            />

            {/* 新增门禁信息确认弹窗 */}
            <Dialog
                visible={addAccessInfoDialogVisible}
                content={
                    <div className="py-2">
                        <div className="text-base font-semibold mb-3 text-center">
                            {t('pages.delivery.orderDetail.addAccessInfoTitle')}
                        </div>
                        <div className="text-base mb-4 text-center">
                            {t('pages.delivery.orderDetail.addAccessInfoConfirm')}
                        </div>
                    </div>
                }
                actions={[
                    [
                        {
                            key: 'cancel',
                            text: t('pages.delivery.orderDetail.cancel'),
                            onClick: () => setAddAccessInfoDialogVisible(false),
                            style: { color: '#4b5563' }
                        },
                        {
                            key: 'confirm',
                            text: t('pages.delivery.orderDetail.confirm'),
                            style: { color: '#60a5fa' },
                            onClick: () => {
                                setAddAccessInfoDialogVisible(false);
                                const currentTrackingNumber = deliveryTaskOrder?.trackingNumber || trackingNumber;
                                const orderId = deliveryTaskOrder?.orderWithAddress?.order?.id;

                                // 如果在地图模式下且有回调，使用回调在抽屉内显示表单
                                if (fromMapMode && onNavigateToAddForm && orderId) {
                                    onNavigateToAddForm({
                                        orderId: orderId,
                                        addressId: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.id,
                                        trackingNumber: currentTrackingNumber,
                                        address: address,
                                        addressLat: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.latitude as number | undefined,
                                        addressLon: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.longitude as number | undefined
                                    });
                                } else {
                                    // 否则使用路由跳转（非地图模式）
                                    navigate(
                                        DeliveryDetailPath.DELIVERY_DELIVERING_ORDER_DETAIL_ADD_ACCESS_CODE.replace(
                                            ':trackingNumber',
                                            currentTrackingNumber
                                        ),
                                        {
                                            state: {
                                                addressId: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.id,
                                                trackingNumber: currentTrackingNumber,
                                                address: address,
                                                addressLat: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.latitude as number | undefined,
                                                addressLon: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.longitude as number | undefined,
                                                orderId: orderId // 传递订单ID
                                            }
                                        }
                                    );
                                }
                            }
                        }
                    ]
                ]}
                onClose={() => setAddAccessInfoDialogVisible(false)}
            />

            {/* 有效性确认弹窗 */}
            <AccessCodeValidationDialog
                visible={validationDialogVisible}
                pendingAccessCodes={pendingAccessCodes}
                onValidationComplete={handleValidationComplete}
                onClose={() => setValidationDialogVisible(false)}
            />
        </div>
    );
});

OrderDetailCard.displayName = 'OrderDetailCard';

export default OrderDetailCard;
